/**
 * Your account: the name and face that follow you everywhere.
 *
 * NOT THE SAME AS YOUR NAME IN A HOUSEHOLD, and the screen says so rather than
 * leaving people to discover it. `auth_user.name` is who you are as a person;
 * `member.display_name` is what one household calls you. Two households may
 * reasonably call you different things — "Hasib" at home, "Hasib S." in the
 * flat — so they are edited in different places on purpose.
 *
 * THE PICTURE IS AN EMOJI, or the photo your provider gave. This app stores no
 * images at all (§3.6): an upload would need a blob store, size and type
 * limits, RLS on the blobs and a serving route, which is a subsystem for a
 * decoration. Signing in with Google brings a photo along for free; everyone
 * else picks a glyph, which exports, renders offline and costs nothing.
 */
import { useEffect, useState } from 'react';
import { authApi, messageOf } from './api.js';

/** A starting set, not a limit — the field below takes anything. */
const SUGGESTED = [
  '\u{1F600}', '\u{1F60E}', '\u{1F913}', '\u{1F9D4}', '\u{1F471}', '\u{1F9D1}',
  '\u{1F431}', '\u{1F436}', '\u{1F98A}', '\u{1F989}', '\u{1F433}', '\u{1F41D}',
  '\u{1F334}', '\u{1F344}', '\u{1F33B}', '☘️', '\u{1F30D}', '\u{1F319}',
  '⚽', '\u{1F3B8}', '\u{1F3A8}', '\u{1F4DA}', '☕', '\u{1F680}',
];

interface Me {
  id: string;
  name: string;
  email: string;
  image: string | null;
  avatar: string | null;
}

export function Profile({ onChanged }: { onChanged: () => void }) {
  const [me, setMe] = useState<Me | null>(null);
  const [name, setName] = useState('');
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    authApi
      .profile()
      .then((data) => {
        if (cancelled) return;
        setMe(data);
        setName(data.name);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(messageOf(e));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function save(patch: { name?: string; avatar?: string | null }) {
    setBusy(true);
    setError(null);
    try {
      const result = await authApi.updateProfile(patch);
      if (!result.ok) {
        setError(result.message ?? 'That did not save.');
        return;
      }
      const fresh = await authApi.profile();
      setMe(fresh);
      setName(fresh.name);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 1800);
      onChanged();
    } catch (e: unknown) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  }

  if (!me) {
    return (
      <section className="settings">
        <p className="hint">{error ?? 'Loading…'}</p>
      </section>
    );
  }

  const face = me.avatar ?? null;

  return (
    <section className="settings">
      <div className="settings-block">
        <h2>Name</h2>
        <p className="hint">
          Your name as a person. It follows you into every household. What a particular household
          calls you is set on the Household page, and the two are allowed to differ.
        </p>

        {error && <p className="error">{error}</p>}

        <label className="signin-field" style={{ maxWidth: 340 }}>
          <span className="field-label">Your name</span>
          <input
            value={name}
            disabled={busy}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => {
              const next = name.trim();
              if (!next || next === me.name) {
                setName(me.name);
                return;
              }
              void save({ name: next });
            }}
            aria-label="Your name"
          />
        </label>

        <label className="signin-field" style={{ maxWidth: 340 }}>
          <span className="field-label">Email</span>
          {/* Read-only: changing it is a re-verification flow, and there is no
              email sender yet to verify anything with. Shown because "which
              account am I signed in as" is a real question. */}
          <input value={me.email} readOnly disabled aria-label="Email" />
          <span className="hint">Sign in with this. Changing it is not possible yet.</span>
        </label>
      </div>

      <div className="settings-block">
        <h2>Picture</h2>
        <p className="hint">
          An emoji, not an upload — this app keeps no images at all.
          {me.image && ' You signed in with a provider that gave us a photo, which is used unless you pick something here.'}
        </p>

        <div className="profile-face">
          {face ? (
            <span className="avatar avatar-lg" aria-hidden="true">{face}</span>
          ) : me.image ? (
            <img className="avatar avatar-lg" src={me.image} alt="" aria-hidden="true" />
          ) : (
            <span className="avatar avatar-lg is-initial" aria-hidden="true">
              {([...(me.name || me.email).trim()][0] ?? '?').toLocaleUpperCase()}
            </span>
          )}
          <span className="hint">
            {face ? 'Your chosen emoji.' : me.image ? 'From your sign-in provider.' : 'Your initial.'}
            {saved && <strong> · saved</strong>}
          </span>
        </div>

        <div className="icon-grid profile-grid">
          {SUGGESTED.map((emoji) => (
            <button
              key={emoji}
              type="button"
              className={me.avatar === emoji ? 'icon-choice on' : 'icon-choice'}
              disabled={busy}
              onClick={() => void save({ avatar: emoji })}
              aria-label={`Use ${emoji}`}
            >
              {emoji}
            </button>
          ))}
        </div>

        <div className="icon-pop-foot">
          <input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              e.preventDefault();
              const next = typed.trim();
              if (next) void save({ avatar: next });
              setTyped('');
            }}
            placeholder="or paste one"
            aria-label="Type or paste an emoji"
            maxLength={12}
          />
          {me.avatar && (
            <button
              type="button"
              className="chip chip-ghost"
              disabled={busy}
              onClick={() => void save({ avatar: null })}
            >
              Clear
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
